// Utilities for multi-ayah range selection inside the Reader.
//
// A `selection` is `{ anchor: { s, a }, endpoint: { s, a } | null }`.
// Anchor is the first tap; endpoint is the second tap. `pageAyahs` is the
// ordered list of ayahs currently rendered on the page/spread — order
// determines the inclusive range.

/**
 * Compute the inclusive set of "{surah}:{ayah}" keys covered by a selection.
 * Order is determined by the ayah's index in `pageAyahs`, which mirrors
 * reading order across the visible Mushaf spread (or Surah Mode list).
 *
 * @param {Array<{surahNumber:number, numberInSurah:number}>} pageAyahs
 * @param {{anchor:{s:number,a:number}, endpoint:{s:number,a:number}|null}|null} selection
 * @returns {Set<string>|null}
 */
export function computeSelectedKeys(pageAyahs, selection) {
  if (!selection || pageAyahs.length === 0) return null;
  const keyOf = (s, a) => `${s}:${a}`;
  const idx = (s, a) =>
    pageAyahs.findIndex((x) => x.surahNumber === s && x.numberInSurah === a);
  const aIdx = idx(selection.anchor.s, selection.anchor.a);
  if (aIdx < 0) return null;
  if (!selection.endpoint) {
    return new Set([keyOf(selection.anchor.s, selection.anchor.a)]);
  }
  const eIdx = idx(selection.endpoint.s, selection.endpoint.a);
  if (eIdx < 0) return new Set([keyOf(selection.anchor.s, selection.anchor.a)]);
  const lo = Math.min(aIdx, eIdx);
  const hi = Math.max(aIdx, eIdx);
  const out = new Set();
  for (let i = lo; i <= hi; i++) {
    out.add(keyOf(pageAyahs[i].surahNumber, pageAyahs[i].numberInSurah));
  }
  return out;
}
